import { useCallback, useEffect, useRef, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { EyeIcon, EyeOffIcon } from "./Icons";
import { shortTime } from "./format";
import Avatar from "./Avatar";
import PopoverAnchor, { useClampToViewport } from "./Popover";

export type DmReceipt = "sent" | "delivered" | "read";

const DM_LABEL: Record<DmReceipt, string> = {
  sent: "Sent",
  delivered: "Delivered",
  read: "Seen",
};

/** 1:1 read state on your own message: open eye once they've opened it,
 * struck-through eye until then. */
export function DmSeenIndicator({ state }: { state: DmReceipt }) {
  const label = DM_LABEL[state];
  return (
    <span className={`seen seen--${state}`} title={label} aria-label={label}>
      {state === "read" ? <EyeIcon size={13} /> : <EyeOffIcon size={13} />}
    </span>
  );
}

type Reader = { uid: string; name: string; at: number };
type Pending = { uid: string; name: string };

/**
 * Group read state on your own message: an eye + the number of members who
 * have opened it, clickable for the actual list of who has — and, just as
 * usefully, who hasn't yet.
 *
 * Fetches for every own message rendered, not just the newest — an
 * earlier version only prefetched the newest one and left older own
 * messages showing a bare eye with no count until clicked, which read as
 * a bug (two visually similar messages in different states for no
 * apparent reason) rather than a deliberate loading state. Consistency
 * across all of them is worth the extra CometChat.getMessageReceipts
 * calls at this scale.
 */
export function GroupSeenIndicator({
  messageId,
  guid,
  livePulse,
}: {
  messageId: number;
  guid?: string;
  /** Bumped by the parent on every group read receipt it sees — the only
   * signal this has that someone just read something, since the reader
   * list itself comes from a one-off REST call with no listener of its
   * own. Without it, a message's "seen by" only ever reflected whoever
   * had already read it at the moment this row first mounted. */
  livePulse?: number;
}) {
  const [readers, setReaders] = useState<Reader[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  // Everyone in the group except you — used to derive the "not seen yet"
  // half, which receipts alone can't tell you (a member who hasn't opened
  // the message simply has no receipt to return).
  const [members, setMembers] = useState<Pending[] | null>(null);
  // Same viewport-edge overflow the emoji/reminder popovers had — this one
  // never got the fix, so it could still run past the pane's edge or off
  // the bottom depending on where in the message list you clicked it.
  useClampToViewport(popRef, [open, readers, members]);
  // Guards against a second in-flight fetch and against the load effect
  // re-firing — plain state would need to be in the callback's deps, which
  // reintroduces the loop this avoids.
  const attempted = useRef(false);

  const load = useCallback(async () => {
    if (attempted.current) return;
    attempted.current = true;
    setLoading(true);
    setError(false);
    try {
      const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
      const raw = (await CometChat.getMessageReceipts(String(messageId))) as unknown;
      const list = (Array.isArray(raw) ? raw : []) as CometChat.MessageReceipt[];
      // The API returns a delivery receipt AND a read receipt per member;
      // keep only reads, one row per person, earliest read wins.
      const byUid = new Map<string, Reader>();
      for (const r of list) {
        const at = Number(r.getReadAt?.() || 0);
        if (!at) continue;
        const sender = r.getSender?.();
        const uid = sender?.getUid?.();
        if (!uid || uid === meUid) continue;
        const prev = byUid.get(uid);
        if (!prev || at < prev.at) byUid.set(uid, { uid, name: sender.getName?.() || uid, at });
      }
      setReaders([...byUid.values()].sort((a, b) => a.at - b.at));
    } catch (e) {
      console.warn("read receipts fetch failed", e);
      setError(true);
      attempted.current = false; // let a click retry after a failed fetch
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Refetch whenever a live read receipt comes in for this group — the
  // very first fetch above only catches whoever had already read it by
  // the time this row mounted.
  const prevPulse = useRef(livePulse);
  useEffect(() => {
    if (livePulse === undefined || prevPulse.current === livePulse) return;
    prevPulse.current = livePulse;
    attempted.current = false;
    void load();
  }, [livePulse, load]);

  // Members load lazily on first open — the count on the closed indicator
  // only needs receipts, so there's no reason to pull a member list for
  // every message in the pane.
  useEffect(() => {
    if (!open || !guid || members !== null) return;
    let cancelled = false;
    const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
    new CometChat.GroupMembersRequestBuilder(guid)
      .setLimit(100)
      .build()
      .fetchNext()
      .then((list) => {
        if (cancelled) return;
        setMembers(
          (list as CometChat.GroupMember[])
            .filter((m) => m.getUid() !== meUid)
            .map((m) => ({ uid: m.getUid(), name: m.getName() || m.getUid() }))
        );
      })
      .catch((e) => {
        console.warn("receipt member list failed", e);
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, guid, members]);

  const count = readers?.length ?? null;
  const seenUids = new Set((readers || []).map((r) => r.uid));
  const pending = (members || []).filter((m) => !seenUids.has(m.uid));

  return (
    <PopoverAnchor active={open} onDismiss={() => setOpen(false)}>
      <button
        className={`seen seen--group ${count ? "seen--read" : ""}`}
        title={count === null ? "See who's read this" : `Seen by ${count}`}
        aria-label={count === null ? "See who's read this" : `Seen by ${count}`}
        onClick={() => {
          setOpen((v) => !v);
          void load();
        }}
      >
        {count ? <EyeIcon size={13} /> : <EyeOffIcon size={13} />}
        {count !== null && <b>{count}</b>}
      </button>
      {open && (
        <div className="seen-pop" ref={popRef}>
          <div className="seen-pop__head">
            {loading && readers === null
              ? "Loading…"
              : error
              ? "Couldn't load"
              : `Seen by ${readers?.length ?? 0}`}
          </div>
          {readers?.length === 0 && !loading && !error && (
            <div className="seen-pop__empty">No one has opened this yet</div>
          )}
          {readers?.map((r) => (
            <div className="seen-pop__row" key={r.uid}>
              <Avatar uid={r.uid} name={r.name} size="dav" />
              <span className="nm">{r.name}</span>
              <time>{shortTime(r.at)}</time>
            </div>
          ))}
          {pending.length > 0 && (
            <>
              <div className="seen-pop__head seen-pop__head--sub">Not seen yet</div>
              {pending.map((m) => (
                <div className="seen-pop__row seen-pop__row--pending" key={m.uid}>
                  <Avatar uid={m.uid} name={m.name} size="dav" />
                  <span className="nm">{m.name}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </PopoverAnchor>
  );
}
